import PaymentValidator from 'payment';
import * as validatorModule from 'validator';

const { isEmail, isSlug, isURL } = validatorModule.default || validatorModule;

import { messages } from '../../_shared/i18n/messages';

import { validateField } from '../hooks/useFormValidation';

export const tcFormValidators = {
	required: validateRequired,
	requiredConditional: validateRequiredConditional,
	length: validateLength,
	matches: validateMatch,
	number: validateNumber,
	email: validateEmail,
	slug: validateSlug,
	url: validateURL,
	creditCardNumber: validateCreditCardNumber,
	creditCardSecurityCode: validateCreditCardSecurityCode,
	creditCardExpirationDate: validateCreditCardExpirationDate,
	regexp: validateRegexp,
	arrayContainingObjects: validateArrayContainingObjects,
};

/**
 * Checks if a value is empty.
 *
 * @param {Any}  value The value to check.
 *
 * @returns {Boolean} The result.
 */
function _isEmpty(value) {

	if (
		value === undefined ||
		value === null ||
		value === '' ||
		value === false
	) {
		return true;
	}

	if (Array.isArray(value) && value.length === 0) {
		return true;
	}

	return false;

}

/**
 * Checks to make sure that a required value is provided.
 *
 * @param {string} value  The new value.
 * @param {object} data   The parent data, i.e. the form data.
 * @param {object} intl   The provider for internationalization.
 *
 * @returns {array} result
 * @returns {boolean} result[0]  True, if value is valid.
 * @returns {string} result[1]  The specific error message (if any).
 */
function validateRequired(value, data, intl) {

	const isValid = !_isEmpty(value);

	const errorMsg =
		isValid ? null : intl.formatMessage(messages.TC_COMPONENTS_FORM_ERROR_REQUIRED);

	return [isValid, errorMsg];

}

/**
 * Checks to make sure that a required value is provided, given
 * additional conditional checks on other values in the form data.
 *
 * @param {func} conditionalChecker  Callback function (returns true/false) for additional
 *                                   conditional checks on other values in the form data.
 *
 * @returns {function} The resulting validator.
 */
function validateRequiredConditional(conditionalChecker) {

	/**
	 * Checks to make sure that a required value is provided.
	 *
	 * @param {string} value  The new value.
	 *
	 * @param {object} data  The parent data, i.e. the form data.
	 * NOTE: If nested inside a "arrayContainingObjects" validator, then it will be the object itself.
	 *
	 * @param {object} intl  The provider for internationalization.
	 *
	 * @returns {array} result
	 * @returns {boolean} result[0]  True, if value is valid.
	 * @returns {string} result[1]  The specific error message (if any).
	 */
	const validate = (value, data, intl) => {

		// Given the current state of the form data,
		// check if this value should be required
		const isRequired = conditionalChecker(data);

		const isValid = !isRequired || !_isEmpty(value);

		const errorMsg =
			isValid ? null : intl.formatMessage(messages.TC_COMPONENTS_FORM_ERROR_REQUIRED);

		return [isValid, errorMsg];

	};

	return validate;

}

/**
 * Validate that a textual input is a certain length.
 *
 * @param {number} min  The minimum length (inclusive).
 * @param {number} max  The maximum length (inclusive).
 *
 * @returns {function} The resulting validator.
 */
function validateLength(min, max) {

	/**
	 * Checks to make sure that a value is valid.
	 *
	 * @param {string} value  The new value.
	 * @param {object} data  The parent data, i.e. the form data.
	 * @param {object} intl  The provider for internationalization.
	 *
	 * @returns {array} result
	 * @returns {boolean} result[0]  True, if value is valid.
	 * @returns {string} result[1]  The specific error message (if any).
	 */
	const validate = (value, data, intl) => {

		const isValid =
			value.length >= min && value.length <= max;

		const errorMsg =
			isValid ? null : intl.formatMessage(messages.TC_COMPONENTS_FORM_ERROR_LENGTH, {
				min,
				max,
			});

		return [isValid, errorMsg];

	};

	return validate;

}

/**
 * Validates that the value matches the value of the passed field.
 *
 * @param {string} fieldName The field to match against.
 *
 * @returns {function} The resulting validator.
 */
function validateMatch(fieldName) {

	/**
	 * Checks to make sure that a value is valid.
	 *
	 * @param {string} value  The new value.
	 * @param {object} data  The parent data, i.e. the form data.
	 * @param {object} intl  The provider for internationalization.
	 *
	 * @returns {array} result
	 * @returns {boolean} result[0]  True, if the value is valid.
	 * @returns {string} result[1]  The specific error message (if any).
	 */
	const validate = (value, data, intl) => {

		const isValid = value === data[fieldName];

		const errorMsg =
			isValid ? null : intl.formatMessage(messages.TC_COMPONENTS_FORM_ERROR_MATCHES, {
				fieldName
			});

		return [isValid, errorMsg];

	};

	return validate;

}

/**
 * Checks to make sure that a number is valid.
 *
 * NOTE: This only works for "integer" values!
 *
 * @param {string} value  The new value.
 * @param {object} data  The parent data, i.e. the form data.
 * @param {object} intl  The provider for internationalization.
 *
 * @returns {array} result
 * @returns {boolean} result[0]  True, if value is valid.
 * @returns {string} result[1]  The specific error message (if any).
 */
function validateNumber(value, data, intl) {

	let isValid = true, errorMsg = null;

	if (!_isEmpty(value)) {

		const parsedValue = parseInt(value, 10);

		isValid = !isNaN(parsedValue);

		errorMsg =
			isValid ? null : intl.formatMessage(messages.TC_COMPONENTS_FORM_ERROR_NUMBER);

	}

	return [isValid, errorMsg];

}

/**
 * Checks to make sure that an email address is valid.
 *
 * @param {string} value  The new value.
 * @param {object} data  The parent data, i.e. the form data.
 * @param {object} intl  The provider for internationalization.
 *
 * @returns {array} result
 * @returns {boolean} result[0]  True, if value is valid.
 * @returns {string} result[1]  The specific error message (if any).
 */
function validateEmail(value, data, intl) {

	const isValid = isEmail(value);

	const errorMsg =
		isValid ? null : intl.formatMessage(messages.TC_COMPONENTS_FORM_ERROR_EMAIL);

	return [isValid, errorMsg];

}

/**
 * Checks to make sure that a slug is valid.
 *
 * @param {string} value  The new value.
 * @param {object} data  The parent data, i.e. the form data.
 * @param {object} intl  The provider for internationalization.
 *
 * @returns {array} result
 * @returns {boolean} result[0]  True, if value is valid.
 * @returns {string} result[1]  The specific error message (if any).
 */
function validateSlug(value, data, intl) {

	const isValid = isSlug(value);

	const errorMsg =
		isValid ? null : intl.formatMessage(messages.TC_COMPONENTS_FORM_ERROR_SLUG);

	return [isValid, errorMsg];

}

/**
 * Checks to make sure that a URL is valid.
 *
 * @param {string} value  The new value.
 * @param {object} data  The parent data, i.e. the form data.
 * @param {object} intl  The provider for internationalization.
 *
 * @returns {array} result
 * @returns {boolean} result[0]  True, if value is valid.
 * @returns {string} result[1]  The specific error message (if any).
 */
function validateURL(value, data, intl) {
	// If empty, it's valid (optional field)
	if (!value) {
		return [true, null];
	}

	// Special case for mailto links
	if (value.toLowerCase().startsWith('mailto:')) {
		// Extract the email part and validate it
		const email = value.substring(7); // Remove 'mailto:' prefix
		const isValid = isEmail(email);

		const errorMsg = isValid ? null : intl.formatMessage(messages.TC_COMPONENTS_FORM_ERROR_URL);

		return [isValid, errorMsg];
	}

	// Regular URL validation
	const isValid = isURL(value);
	const errorMsg = isValid ? null : intl.formatMessage(messages.TC_COMPONENTS_FORM_ERROR_URL);

	return [isValid, errorMsg];
}

/**
 * Checks to make sure that a credit card number is valid.
 *
 *
 * @param {string} value  The new value.
 * @param {object} data  The parent data, i.e. the form data.
 * @param {object} intl  The provider for internationalization.
 *
 * @returns {array} result
 * @returns {boolean} result[0]  True, if the number is valid.
 * @returns {string} result[1]  The specific error message (if any).
 */
function validateCreditCardNumber(value, data, intl) {

	const isValid = PaymentValidator.fns.validateCardNumber(value);

	const errorMsg =
		isValid ? null : intl.formatMessage(messages.TC_COMPONENTS_FORM_ERROR_CREDITCARD_NUMBER);

	return [isValid, errorMsg];

}

/**
 * Checks to make sure that a credit card security code is valid.
 *
 * @param {string} value  The new value.
 * @param {object} data  The parent data, i.e. the form data.
 * @param {object} intl  The provider for internationalization.
 *
 * @returns {array} result
 * @returns {boolean} result[0]  True, if the code is valid.
 * @returns {string} result[1]  The specific error message (if any).
 */
function validateCreditCardSecurityCode(value, data, intl) {

	const isValid = PaymentValidator.fns.validateCardCVC(value);

	const errorMsg =
		isValid ? null : intl.formatMessage(messages.TC_COMPONENTS_FORM_ERROR_CREDITCARD_CVC);

	return [isValid, errorMsg];

}

/**
 * Checks to make sure that a credit card expiration date is valid.
 *
 * @param {string} value  The new value.
 * @param {object} data  The parent data, i.e. the form data.
 * @param {object} intl  The provider for internationalization.
 *
 * @returns {array} result
 * @returns {boolean} result[0]  True, if the expiration date is valid.
 * @returns {string} result[1]  The specific error message (if any).
 */
function validateCreditCardExpirationDate(value, data, intl) {

	if (!value) {
		return [true, null];
	}

	// Parse the month and year pieces from the value
	const expiryDate = PaymentValidator.fns.cardExpiryVal(value);

	const isValid = PaymentValidator.fns.validateCardExpiry(
		expiryDate.month,
		expiryDate.year,
	);

	const errorMsg =
		isValid ? null : intl.formatMessage(messages.TC_COMPONENTS_FORM_ERROR_CREDITCARD_EXPIRY);

	return [isValid, errorMsg];

}

/**
 * Validates that the value matches the value of the passed field.
 *
 * @param {object} options  The regexp validator options.
 *
 * @param {RegExp|string} options.regexp  The regular expression to test against.
 * @param {string}  options.message       The error message to return in case of failure.
 * @param {boolean} options.match         True if we should be matching the regex, or
 *                                        False (default) if we should be erroring on matches.
 * @param {string} options.flags          Flags to pass to the RegExp if it was\
 *                                        specified by a string.
 *
 * @returns {function} The resulting validator.
 */
function validateRegexp(options = {}) {

	/**
	 * Checks to make sure that a regexp value is valid.
	 *
	 * @param {string} value  The new value.
	 * @param {object} data  The parent data, i.e. the form data.
	 * @param {object} intl  The provider for internationalization.
	 *
	 * @returns {array} result
	 * @returns {boolean} result[0]  True, if the value is valid.
	 * @returns {string} result[1]  The specific error message (if any).
	 */
	const validate = (value, data, intl) => {

		let isValid = true;

		let errorMsg = null;

		// Ignore empty strings, undefined, null, etc.
		if (!_isEmpty(value)) {

			let regexpToTest = options.regexp;

			// Create RegExp from string if it is valid
			if (typeof regexpToTest === 'string') {
				regexpToTest = new RegExp(regexpToTest, options.flags);
			}

			// Check if the value is valid given the RegExp
			if (options.match) {
				isValid = !options.regexp.test(value);
			} else {
				isValid = options.regexp.test(value);
			}

			// Build the error message (if any) for the value
			if (isValid) {
				errorMsg = null;
			} else if (options.message) {
				errorMsg = options.message;
			} else {
				errorMsg = intl.formatMessage(messages.TC_COMPONENTS_FORM_ERROR_DEFAULT);
			}

		}

		return [isValid, errorMsg];

	};

	return validate;

}

/**
 * Validates each item of an object array.
 *
 * @param {String} key  Field name to access the unique key for the item's validation data.
 * @param {Object} options  Field validator options where keys are field names
 *                          and values are TC form validator functions.
 *
 * @returns {function} The resulting validator.
 */
function validateArrayContainingObjects(key, options) {

	/**
	 * Checks to make sure that each item in an array has valid values based
	 * on the provided validator options.
	 *
	 * @param {Array} value  The new value.
	 * @param {object} data  The parent data, i.e. the form data.
	 * @param {Object} intl  The provider for internationalization.
	 *
	 * @returns {Array} result
	 * @returns {Boolean} result[0]  True, if the value is valid.
	 * @returns {String} result[1]   The specific error message (if any).
	 * @returns {Object} result[2]   Validation data for each item, keyed by the provided key value.
	 */
	const validate = (value, data, intl) => {

		const result = {};

		let isArrayInvalid = false;

		value.forEach((item) => {

			const keyName = item[key];

			result[keyName] = {};

			const fieldNames = Object.keys(item);

			fieldNames.forEach((fieldName) => {

				if (options[fieldName]) {

					if (Array.isArray(options[fieldName])) {

						result[keyName][fieldName] = { invalid: false };

						const {
							invalid: itemFieldInvalid,
							error: itemFieldError,
							data: itemFieldData,
						} = validateField(item[fieldName], options[fieldName], item, intl);

						// Keep track of validity for overall array
						isArrayInvalid = isArrayInvalid || itemFieldInvalid;

						// Store validation data specific to the item value
						result[keyName][fieldName] = {
							invalid: result[keyName][fieldName].invalid || itemFieldInvalid,
							error: itemFieldError,
							data: itemFieldData,
						};

					// Support additional level of field validation for JSON data
					} else if (typeof options[fieldName] === 'object') {

						// Update validation state for this JSON data field
						const fieldNameValidation = {};

						const subFieldNames = Object.keys(options[fieldName]);

						// Loop through each nested field for a list of validation requirements
						subFieldNames.forEach((subName) => {

							const subFieldRequirements = options[fieldName]?.[subName];
							const subFieldValue = item[fieldName]?.[subName];

							// Perform validation
							const {
								invalid: itemFieldInvalid,
								error: itemFieldError,
								data: itemFieldData,
							} = validateField(
								subFieldValue,
								subFieldRequirements,
								item,
								intl,
							);

							// Update overall form validation state
							isArrayInvalid = isArrayInvalid || itemFieldInvalid;

							// Store validation data specific to the item value
							fieldNameValidation[subName] = {
								invalid: result[keyName]?.[fieldName]?.[subName]?.invalid || itemFieldInvalid,
								error: itemFieldError,
								data: itemFieldData,
							};

						});

						result[keyName][fieldName] = fieldNameValidation;

					}

				}

			});

		});

		const arrayError = isArrayInvalid ? intl.formatMessage(messages.TC_COMPONENTS_FORM_ERROR_DEFAULT) : null;

		return [!isArrayInvalid, arrayError, result];

	};

	return validate;

}
